import { z } from "zod";

/**
 * Medication form validation (#2228).
 *
 * The Add/Edit Medication modal used bare `parseFloat` on dose strings, so
 * "12abc" silently became 12, "1,5" (comma decimal — common from OCR'd
 * European prescriptions) became 1, and zero/negative doses passed through
 * to the API. Dose parsing and the draft schema live here so create, edit,
 * add-another, and OCR paths validate identically and are testable without
 * React Native imports.
 */

export type DoseParseResult =
  | { ok: true; value: number | null }
  | { ok: false; reason: "invalid" | "not_positive" | "too_large" };

/**
 * Upper guard for a single dose amount. Gonadotropin doses are commonly
 * expressed in IU and can reach the low thousands; anything above this is a
 * typo (e.g. a date pasted into the dose field), not a prescription.
 */
export const MAX_DOSE_AMOUNT = 100_000;

/**
 * Parse a user- or OCR-entered dose string.
 *
 * - blank input is valid and means "no dose recorded" (the API field is
 *   nullable);
 * - comma decimals are normalized ("0,5" → 0.5);
 * - anything that is not entirely a positive decimal number is rejected —
 *   no partial `parseFloat` prefixes, no negatives, no zero, no scientific
 *   notation, nothing non-finite.
 */
export function parseDoseInput(
  raw: string | null | undefined,
): DoseParseResult {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { ok: true, value: null };

  const normalized = trimmed.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return { ok: false, reason: "invalid" };
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) return { ok: false, reason: "invalid" };
  if (value <= 0) return { ok: false, reason: "not_positive" };
  if (value > MAX_DOSE_AMOUNT) return { ok: false, reason: "too_large" };
  return { ok: true, value };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const addMedicationSchema = z
  .object({
    name: z.string().trim().min(1, "name_required"),
    /** Raw dose string as typed; parseDoseInput is the single authority. */
    dosageAmount: z.string(),
    doseUnit: z.string().nullable(),
    startDate: z.string().regex(ISO_DATE, "start_date_invalid"),
    endDate: z.string().regex(ISO_DATE, "end_date_invalid").nullable(),
  })
  .superRefine((draft, ctx) => {
    const dose = parseDoseInput(draft.dosageAmount);
    if (!dose.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dosageAmount"],
        message: `dose_${dose.reason}`,
      });
    }
    if (draft.endDate && draft.endDate < draft.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "end_before_start",
      });
    }
  });

export type AddMedicationValues = z.infer<typeof addMedicationSchema>;

export type MedicationDraftField =
  | "name"
  | "dosageAmount"
  | "startDate"
  | "endDate";

export type MedicationDraftValidation =
  | { ok: true; dose: number | null }
  | { ok: false; fieldErrors: Partial<Record<MedicationDraftField, string>> };

/**
 * Validate a medication draft before any API call. Returns the parsed dose
 * on success so submit paths never re-parse with different rules.
 */
export function validateMedicationDraft(draft: {
  name: string;
  dosageAmount: string;
  doseUnit?: string | null;
  startDate: string;
  endDate?: string | null;
}): MedicationDraftValidation {
  const result = addMedicationSchema.safeParse({
    name: draft.name,
    dosageAmount: draft.dosageAmount,
    doseUnit: draft.doseUnit ?? null,
    startDate: draft.startDate,
    endDate: draft.endDate ?? null,
  });

  if (result.success) {
    const dose = parseDoseInput(draft.dosageAmount);
    return { ok: true, dose: dose.ok ? dose.value : null };
  }

  const fieldErrors: Partial<Record<MedicationDraftField, string>> = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (
      (field === "name" ||
        field === "dosageAmount" ||
        field === "startDate" ||
        field === "endDate") &&
      !fieldErrors[field]
    ) {
      fieldErrors[field] = issue.message;
    }
  }
  return { ok: false, fieldErrors };
}
